import React, { useState } from 'react';
import { Modal, Button } from '../shared/Modal';
import { Field, TextInput } from '../shared/FormFields';

// Handing an account over is the one action here that cannot be undone by the
// person doing it — afterwards only the NEW owner can transfer it back. So it
// gets a type-to-confirm rather than a plain "are you sure", and the
// consequences are spelled out rather than summarised.
//
// Not built on ConfirmDialog: that renders its message inside a <p>, which a
// Field cannot legally nest in, and the copy has to branch on mode. The server
// re-checks `confirm` against `email` — this dialog is the courtesy, not the
// enforcement.

const list = { margin: '0 0 16px', paddingLeft: '18px', fontSize: '13px', lineHeight: 1.7, color: 'var(--text-secondary, #64748b)' };

const norm = (v) => String(v || '').trim().toLowerCase();

export default function TransferOwnershipDialog({ email, mode = 'transfer', busy = false, onConfirm, onClose }) {
  const [recipient, setRecipient] = useState('');
  const [typed, setTyped] = useState('');
  const claiming = mode === 'claim';
  // Opened from a row, the recipient is already settled and `email` carries it.
  // Opened from the page header there is no row, so the dialog asks — that is
  // the only way to reach someone the roster does not render.
  const picking = !email;
  const target = picking ? recipient.trim() : email;
  // An empty target must never satisfy an empty confirmation.
  const matches = target.includes('@') && norm(typed) === norm(target);

  const confirm = () => {
    if (!matches || busy) return;
    onConfirm({ email: target, confirm: typed.trim() });
  };

  return (
    <Modal
      title={claiming ? 'Set the account owner' : 'Transfer ownership'}
      width={480}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={confirm} disabled={!matches || busy}>
            {busy ? 'Working…' : claiming ? 'Set owner' : 'Transfer ownership'}
          </Button>
        </>
      )}
    >
      <ul style={list}>
        {claiming && <li>This account has no recorded owner.</li>}
        <li><strong>{target || 'The person you name'}</strong> becomes the owner: full access, plus the team and workspaces.</li>
        {claiming
          ? <li>Nobody else&apos;s access changes.</li>
          : (
            <>
              <li>You become an Admin — you keep everything except the ability to transfer ownership.</li>
              <li>From then on, only the new owner can transfer it back.</li>
            </>
          )}
      </ul>

      {picking && (
        <Field label="Who should own this account?" hint="They must already be on this account — invite them first if they aren't.">
          <TextInput
            type="email"
            value={recipient}
            onChange={setRecipient}
            placeholder="someone@company.com"
          />
        </Field>
      )}

      <Field label={target ? `Type ${target} to confirm` : 'Type their address again to confirm'}>
        <TextInput
          type="email"
          value={typed}
          onChange={setTyped}
          placeholder={target}
          onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
        />
      </Field>
    </Modal>
  );
}
